import type { ArrayOperand, NumberOperand, StringOperand, Operand } from '../types/condition';
import type { ConditionGroupInput } from '../types/condition-group-input';
import type { ConditionGroupPatch } from '../types/condition-group-patch';
import type {
  ArrayConditionInput,
  PricingTableSummaryConditionInput,
  ConditionInput,
  DocumentConditionInput,
  PricingTableRowConditionInput,
  RecipientConditionInput,
} from '../types/condition-input';
import type { ConditionPatch } from '../types/condition-patch';
import type { ConditionalEffectPatch } from '../types/conditional-effect-patch';
import type { CreateTemplateConditionalEffectPayload } from '../types/create-template-conditional-effect-payload';
import type { EffectInput, EffectPatch } from '../types/effect-input';
import type {
  ConditionalContentNodeSelection,
  ConditionalContentRowSelection,
  ConditionalContentSectionSelection,
  ConditionalContentSelection,
} from '../types/selection-payload';

export const isArrayValue = (operand: Operand): operand is ArrayOperand =>
  operand.type === 'Recipients';

export const isStringValue = (operand: Operand): operand is StringOperand =>
  operand.type === 'String';

export const isNumberValue = (operand: Operand): operand is NumberOperand =>
  operand.type === 'Number';

export const isRecipientConditionInput = (
  input: ConditionInput | ConditionPatch
): input is RecipientConditionInput => 'recipientOperand' in input;

export const isPricingTableRowConditionInput = (
  input: ConditionInput
): input is PricingTableRowConditionInput => 'pricingTableRowOperand' in input;

export const isPricingTableSummaryConditionInput = (
  input: ConditionInput
): input is PricingTableSummaryConditionInput => 'pricingTableSummaryOperand' in input;

export const isArrayConditionInput = (input: ConditionInput): input is ArrayConditionInput =>
  isRecipientConditionInput(input) || isPricingTableRowConditionInput(input);

export const isDocumentConditionInput = (input: ConditionInput): input is DocumentConditionInput =>
  'documentOperand' in input;

export const isNodeSelection = (
  selection: ConditionalContentSelection
): selection is ConditionalContentNodeSelection => 'nodeId' in selection;

export const isRowSelection = (
  selection: ConditionalContentSelection
): selection is ConditionalContentRowSelection => 'rowId' in selection && !('nodeId' in selection);

export const isSectionSelection = (
  selection: ConditionalContentSelection
): selection is ConditionalContentSectionSelection =>
  'sectionId' in selection && !('rowId' in selection);

export const isConditionPatch = (
  condition: ConditionInput | ConditionPatch
): condition is ConditionPatch => 'id' in condition;

export const isConditionGroupPatch = (conditionGroup: ConditionGroupInput | ConditionGroupPatch) =>
  'id' in conditionGroup;

export const isConditionalEffectPatch = (
  conditionalEffect: ConditionalEffectPatch | CreateTemplateConditionalEffectPayload
): conditionalEffect is ConditionalEffectPatch => 'id' in conditionalEffect;

export const isEffectPatch = (effect: EffectInput | EffectPatch): effect is EffectPatch =>
  'id' in effect;
export const isEffectInput = (effect: EffectInput | EffectPatch): effect is EffectInput =>
  !('id' in effect);
